import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { twoFactorService } from '../services/twoFactorService';

/**
 * Everything the profile page does to the server, in one place, so the cards
 * stay presentational.
 *
 * Each call refreshes the cached user afterwards where the change is visible
 * elsewhere in the app (the avatar initials, the 2FA state), so the UI never
 * shows a stale copy of something the server just changed.
 */
export function useProfile() {
  const { user, applyUser } = useAuth();

  const saveProfile = useCallback(
    async (firstName: string, lastName: string) => {
      // The endpoint returns the updated record, so use it directly. A second
      // GET /auth/me could fail and leave the card claiming success over stale
      // values on screen.
      const updated = await userService.updateOwnProfile({ firstName, lastName });
      applyUser(updated);
    },
    [applyUser]
  );

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await userService.changePassword({ currentPassword, newPassword });
  }, []);

  const startTwoFactorSetup = useCallback(async () => {
    const { email } = await twoFactorService.startSetup();
    return email;
  }, []);

  const confirmTwoFactorSetup = useCallback(
    async (code: string) => {
      applyUser(await twoFactorService.confirmSetup(code));
    },
    [applyUser]
  );

  const disableTwoFactor = useCallback(async () => {
    applyUser(await twoFactorService.disable());
  }, [applyUser]);

  return {
    user,
    saveProfile,
    changePassword,
    startTwoFactorSetup,
    confirmTwoFactorSetup,
    disableTwoFactor,
  };
}
